import React, { useState, useMemo } from 'react';
import { Transaction, CoinListItem } from '../../types';
import { useCurrency } from '../../context/CurrencyContext';
import { useCoinList } from '../../hooks/useCoinList';
import { loadCoinImages } from '../../utils/coinImageCache';
import {
  captureEntryFxForDate,
  captureFeeForDate,
  captureTransferFxForDate,
  compactFx,
  preserveFee,
  preserveRates,
} from '../../utils/fxCapture';
import type { FeeCapture, PricedFxCapture } from '../../utils/fxCapture';
import { txFeeInDisplay, txNetworkFeeInDisplay, txPriceInDisplay } from '../../utils/displayAmount';
import { CoinSelector } from './CoinSelector';
import { PRESET_WALLETS } from './WalletSelector';
import { collectWallets, fmtCoinShort } from './walletUtils';
import {
  maxSellableAt,
  walletsForCoin,
  newlyUnfunded,
  formatDateAU,
  formatCoinAmount,
  WALLET_EPS,
} from '../../utils/walletTimeline';
import { X, Save, ArrowRightLeft } from 'lucide-react';

export function EditTransactionModal({ tx, metals, transactions, onSave, onClose }: {
  tx: Transaction;
  metals: CoinListItem[];
  transactions: Transaction[];
  onSave: (id: string, updates: Omit<Transaction, 'id' | 'timestamp'>) => void;
  onClose: () => void;
}) {
  const today = new Date().toISOString().split('T')[0];
  const { fmt, currency, rate, rates } = useCurrency();
  const { coins, loading: coinsLoading, error: coinsError } = useCoinList();

  // The price field is shown/entered in the user's DISPLAY currency (matching the
  // Add form). Prefilled through the row's OWN rate — or straight from its raw entry
  // amount when the row was entered in the currency on screen — so reopening a
  // transaction shows the figure that was typed, not that figure re-converted at
  // today's rate. Keeping the exact prefill string is what makes an untouched price
  // round-trip to the identical stored USD.
  const initialPriceStr = String(
    parseFloat(txPriceInDisplay(tx, currency.code, rate).value.toFixed(8))
  );
  // Same treatment for the transfer network fee. It is stored only in USD, but the
  // amount as typed is recoverable from the row's own rate, so tier 1 still applies.
  const initialFeeStr =
    tx.networkFee != null
      ? String(parseFloat(txNetworkFeeInDisplay(tx, currency.code, rate).value.toFixed(8)))
      : '';
  /**
   * The BUY/SELL trading fee prefill.
   *
   * The fee stores the RAW amount and its currency, so when the row was entered in
   * the currency on screen there is nothing to convert and the round-trip is exact
   * by definition — no arithmetic, so no drift is possible. A row entered in a
   * different currency converts at that row's own date.
   */
  const initialTradeFeeStr =
    tx.fee == null
      ? ''
      : String(parseFloat(txFeeInDisplay(tx, currency.code, rate).value.toFixed(8)));

  const NO_WALLET   = '__none__';
  const ADD_CUSTOM  = '__custom__';

  const seedCoin: CoinListItem = useMemo(() => ({
    id: tx.coinId, symbol: tx.symbol, name: tx.name,
    image: loadCoinImages()[tx.coinId] ?? '',
    marketCapRank: 0, currentPrice: tx.price,
    marketCap: 0, volume24h: 0, high24h: 0, low24h: 0,
  }), [tx]);

  const [type,         setType]         = useState<'BUY' | 'SELL' | 'TRANSFER'>(tx.type);
  const [selectedCoin, setSelectedCoin] = useState<CoinListItem>(seedCoin);
  const [amount,       setAmount]       = useState(String(tx.amount));
  const [price,        setPrice]        = useState(initialPriceStr);
  const [date,         setDate]         = useState(tx.date);
  const [wallet,       setWallet]       = useState(tx.wallet ?? '');
  const [tradeFee,     setTradeFee]     = useState(initialTradeFeeStr);
  const [fromWallet,   setFromWallet]   = useState(tx.fromWallet ?? '');
  const [toWallet,     setToWallet]     = useState(tx.toWallet ?? '');
  const [networkFee,   setNetworkFee]   = useState(initialFeeStr);
  const [notes,        setNotes]        = useState(tx.notes ?? '');
  const [formError,    setFormError]    = useState('');

  // BUY/SELL wallet picker — toggles to a free-text input when "Add custom…" is chosen
  const initialWalletIsCustom = !!(tx.wallet && !PRESET_WALLETS.includes(tx.wallet));
  const [showCustomWallet, setShowCustomWallet] = useState(initialWalletIsCustom);

  // TRANSFER From/To inline "Add Custom Wallet" inputs
  const [showCustomFrom, setShowCustomFrom] = useState(false);
  const [customFromInput, setCustomFromInput] = useState('');
  const [showCustomTo,   setShowCustomTo]   = useState(false);
  const [customToInput,  setCustomToInput]  = useState('');

  const knownWallets = useMemo(() => collectWallets(transactions), [transactions]);

  // Per-wallet balance for the selected coin — drives the From-wallet dropdown labels
  // Date-aware throughout, and excluding the row being edited so it is not
  // measured against its own old value. See utils/walletTimeline.
  const balanceByWallet = useMemo(() => {
    const map: Record<string, number> = {};
    for (const w of knownWallets) {
      map[w] = maxSellableAt(transactions, selectedCoin.id, w, date, tx.id);
    }
    return map;
  }, [knownWallets, selectedCoin.id, transactions, tx.id]);

  const fromBalance = type === 'TRANSFER' && fromWallet
    ? maxSellableAt(transactions, selectedCoin.id, fromWallet, date, tx.id)
    : 0;

  /** SELL: what this wallet can fund on the chosen date, this row aside. */
  const sellBalance = type === 'SELL' && wallet
    ? maxSellableAt(transactions, selectedCoin.id, wallet, date, tx.id)
    : 0;

  const amountNum = parseFloat(amount);
  const overLimit =
    !!amount && !isNaN(amountNum) &&
    ((type === 'SELL' && !!wallet && amountNum > sellBalance + WALLET_EPS) ||
      (type === 'TRANSFER' && !!fromWallet && amountNum > fromBalance + WALLET_EPS));

  /**
   * Would saving these fields strand a transaction that depends on this row?
   *
   * Lowering a BUY, moving it later, moving it to another wallet or changing its
   * coin all quietly defund whatever was sold out of it afterwards. The check
   * replays the whole history with the proposed row swapped in and reports only
   * what BREAKS, so data that is already oversold stays editable.
   */
  const dependentsBrokenBy = (amt: number): string | null => {
    const proposed: Transaction = {
      ...tx,
      coinId: selectedCoin.id,
      symbol: selectedCoin.symbol,
      name: selectedCoin.name,
      type,
      amount: amt,
      date,
      wallet: type === 'TRANSFER' ? undefined : (wallet || undefined),
      fromWallet: type === 'TRANSFER' ? fromWallet : undefined,
      toWallet: type === 'TRANSFER' ? toWallet : undefined,
    };
    const after = transactions.map((t) => (t.id === tx.id ? proposed : t));
    // The row being edited is excluded: its own limit is reported by the
    // date-aware guard above, in terms the user can act on.
    const broken = newlyUnfunded(transactions, after).filter((u) => u.txId !== tx.id);
    if (!broken.length) return null;
    const first = broken[0];
    const label = `your ${formatCoinAmount(first.amount)} ${first.symbol} ${first.type === 'TRANSFER' ? 'transfer' : 'sell'} on ${formatDateAU(first.date)}`;
    return broken.length === 1
      ? `This would leave ${label} without enough coins.`
      : `This would leave ${broken.length} later transactions without enough coins, starting with ${label}.`;
  };

  /** Same wording as the Add form, so the two guards read identically. */
  const limitMessage = (w: string, max: number, verb: 'sell' | 'transfer') =>
    max > WALLET_EPS
      ? `You can ${verb} at most ${formatCoinAmount(max)} ${selectedCoin.symbol} from ${w} on ${formatDateAU(date)}.`
      : `You held no ${selectedCoin.symbol} in ${w} on ${formatDateAU(date)}.`;

  /**
   * Coins this row could be a SELL of: anything held, plus whatever it already
   * is. Keeping the current coin selectable is what lets an existing bad sell be
   * opened and corrected rather than becoming uneditable.
   */
  const sellableCoins = useMemo(() => {
    // Sourced from the transaction history rather than today's holdings: a
    // back-dated sell of a coin since fully disposed of is legitimate, and a
    // holdings-based list would hide it.
    const ids = new Set(transactions.map((t) => t.coinId));
    const owned = Array.from(ids)
      .filter((id) => id !== selectedCoin.id)
      .map((id) => {
        const match = coins.find((c) => c.id === id) ?? metals.find((m) => m.id === id);
        if (match) return match;
        const any = transactions.find((t) => t.coinId === id)!;
        return {
          id, symbol: any.symbol, name: any.name, image: '',
          marketCapRank: 0, currentPrice: 0, marketCap: 0, volume24h: 0, high24h: 0, low24h: 0,
        } as CoinListItem;
      });
    // The row's current coin stays selectable whatever its state, or an existing
    // bad sell could be opened but never corrected.
    return [selectedCoin, ...owned];
  }, [transactions, coins, metals, selectedCoin]);

  const handleCoinSelect = (coin: CoinListItem) => {
    setSelectedCoin(coin);
    // coin.currentPrice is USD; the field is in the display currency.
    if (coin.currentPrice > 0) setPrice(String(parseFloat((coin.currentPrice * rate).toFixed(8))));
  };

  /**
   * FX capture for an edit.
   *
   * Two rules, both deliberate:
   *
   * 1. Untouched-field preservation accounts for the CURRENCY as well as the
   *    amount. The price field is prefilled by converting the stored USD into
   *    whatever display currency is active now, which may not be the currency
   *    the row was entered in. A field the user did not touch is not an edit
   *    just because it is being rendered in a different currency — so an
   *    untouched price keeps the original entryAmount/entryCurrency rather than
   *    silently re-denominating the row into today's display currency.
   *
   * 2. Rates re-resolve if and only if the DATE or the ENTRY CURRENCY changed.
   *    They depend on those two and not on the amount, so editing a quantity or
   *    a wallet leaves them alone, and merely reopening a row and saving it
   *    never re-stamps it.
   *
   * 3. When rates DO re-resolve, they resolve for the transaction's new date,
   *    and the stored USD value is recomputed from that same rate in the same
   *    step. Rate and derived price never move independently — that was the
   *    inconsistency the spot-capture stage had to leave open, and it closes
   *    here.
   *
   * "Edited" means the user typed into a currency-denominated field, which makes
   * the active display currency an observed fact for this row. Without that, a
   * row that has no stored entryCurrency stays unknown — inferring a legacy
   * row's currency is out of scope here.
   */
  /**
   * Rates carried over unchanged, with the derived USD value recomputed from
   * them so `price === entryAmount × fxRateUsd` still holds after the edit.
   *
   * `amount` is the figure the user typed in `entryCurrency` — a price per unit
   * on BUY/SELL, the network fee on a TRANSFER. `null` when there is nothing to
   * derive from, in which case the caller keeps the stored USD value verbatim.
   */
  const fxPreserved = (entryCurrency: string, amount: number | undefined): PricedFxCapture => {
    const kept = preserveRates(tx);
    return {
      fx: compactFx({
        ...(amount != null ? { entryAmount: amount } : {}),
        entryCurrency,
        ...kept,
      }),
      priceUsd: amount != null && kept.fxRateUsd != null ? amount * kept.fxRateUsd : null,
    };
  };

  /** FX for an edited BUY/SELL price. See the rules above `fxForEdit`'s doc. */
  const fxForPriceEdit = async (
    priceEdited: boolean,
    entryAmount: number | undefined
  ): Promise<PricedFxCapture> => {
    const entryCurrency = priceEdited ? currency.code : tx.entryCurrency;
    if (entryCurrency == null) return { fx: {}, priceUsd: null };

    const rateInputsChanged = date !== tx.date || entryCurrency !== tx.entryCurrency;

    // Moving the date is exactly the case the date-correct lookup exists for:
    // re-resolve for the NEW date, and recompute the stored USD price from the
    // rate that was just resolved — the two always move together.
    if (rateInputsChanged && entryAmount != null) {
      return captureEntryFxForDate(entryAmount, entryCurrency, date, rates);
    }
    // An untouched price on an unchanged date returns priceUsd: null, so the
    // stored USD is kept byte-for-byte and a no-op edit never drifts.
    return fxPreserved(entryCurrency, priceEdited ? entryAmount : undefined);
  };

  /**
   * The BUY/SELL trading fee, under the same two rules as price: an untouched
   * field is preserved verbatim, and rates re-resolve only when the DATE or the
   * fee's own currency changed.
   *
   * Returning `{}` clears the fee, which is what an edit to empty or zero should
   * do — `onSave` receives a whole replacement object, so an omitted field is a
   * removed field.
   */
  const feeForEdit = async (feeEdited: boolean, typedFee: number): Promise<FeeCapture> => {
    const rawFee = feeEdited ? typedFee : tx.fee;
    if (!(rawFee != null && rawFee > 0 && isFinite(rawFee))) return {};

    // Editing the fee makes the currency on screen an observed fact for it; an
    // untouched fee keeps whatever currency it was entered in.
    const feeCurrency = feeEdited ? currency.code : tx.feeCurrency;
    // A stored fee with no currency cannot be converted or re-resolved. Keeping it
    // verbatim beats guessing which currency it was.
    if (!feeCurrency) return preserveFee(tx);

    const rateInputsChanged = date !== tx.date || feeCurrency !== tx.feeCurrency;
    if (feeEdited || rateInputsChanged) {
      return captureFeeForDate(rawFee, feeCurrency, date, rates);
    }
    return preserveFee(tx);
  };

  /** FX for a TRANSFER, whose only currency-denominated field is the fee. */
  const fxForTransferEdit = async (
    feeEdited: boolean,
    feeAmount: number
  ): Promise<PricedFxCapture> => {
    const entryCurrency = feeEdited ? currency.code : tx.entryCurrency;
    if (entryCurrency == null) return { fx: {}, priceUsd: null };

    const rateInputsChanged = date !== tx.date || entryCurrency !== tx.entryCurrency;

    if (rateInputsChanged) {
      return captureTransferFxForDate(entryCurrency, date, rates, feeAmount);
    }
    // A transfer records no entryAmount (there is no price per unit), so the fee
    // is re-derived only from a rate that is already stored.
    const preserved = fxPreserved(entryCurrency, undefined);
    const keptUsd = preserved.fx.fxRateUsd;
    return {
      fx: preserved.fx,
      priceUsd: feeEdited && keptUsd != null ? feeAmount * keptUsd : null,
    };
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    const amt = parseFloat(amount);
    if (!amt || isNaN(amt) || amt <= 0) { setFormError('Enter a valid amount.'); return; }
    if (!date)                          { setFormError('Select a date.');        return; }

    if (type === 'TRANSFER') {
      if (!fromWallet)               { setFormError('Select a “From” wallet.'); return; }
      if (!toWallet)                 { setFormError('Select a “To” wallet.');   return; }
      if (fromWallet === toWallet)   { setFormError('From and To wallet cannot be the same.'); return; }
      if (amt > fromBalance + WALLET_EPS) {
        setFormError(limitMessage(fromWallet, fromBalance, 'transfer'));
        return;
      }
      const transferBreaks = dependentsBrokenBy(amt);
      if (transferBreaks) { setFormError(transferBreaks); return; }
      const feeNum = networkFee ? parseFloat(networkFee) : 0;
      if (networkFee && (isNaN(feeNum) || feeNum < 0)) { setFormError('Network fee must be ≥ 0.'); return; }
      // Field is in the display currency → store USD, derived from the same rate
      // the row records. An untouched fee keeps the original stored USD exactly.
      const feeEdited = networkFee !== initialFeeStr;
      const transferFx = await fxForTransferEdit(feeEdited, feeNum);
      const feeUsd =
        feeNum > 0
          ? transferFx.priceUsd ?? (feeEdited ? feeNum / rate : tx.networkFee)
          : undefined;
      onSave(tx.id, {
        coinId:     selectedCoin.id,
        symbol:     selectedCoin.symbol,
        name:       selectedCoin.name,
        type:       'TRANSFER',
        amount:     amt,
        price:      0,
        date,
        fromWallet,
        toWallet,
        networkFee: feeUsd,
        notes:      notes.trim() || undefined,
        // No price per unit on a transfer, so no entryAmount. Editing the fee
        // makes the display currency an observed fact for this row.
        ...transferFx.fx,
      });
      onClose();
      return;
    }

    // SELL had NO balance check at all before this, in either direction: the
    // amount could be raised freely, and the type toggle could turn a BUY into a
    // SELL without anything revalidating. Both go through the same date-aware
    // guard as the Add form now.
    if (type === 'SELL') {
      if (!wallet) { setFormError('Select a wallet to sell from.'); return; }
      if (amt > sellBalance + WALLET_EPS) {
        setFormError(limitMessage(wallet, sellBalance, 'sell'));
        return;
      }
    }

    const breaks = dependentsBrokenBy(amt);
    if (breaks) { setFormError(breaks); return; }

    const prc = parseFloat(price);
    if (!prc || isNaN(prc) || prc <= 0) { setFormError('Enter a valid price.'); return; }
    // Field is in the display currency → convert to USD (the storage unit) with the
    // same live rate the Add form uses. If the price field is untouched, keep the
    // original stored USD exactly so an unchanged edit never drifts (USD selected →
    // rate 1 → conversion is already a no-op).
    const tradeFeeNum = tradeFee ? parseFloat(tradeFee) : 0;
    if (tradeFee && (isNaN(tradeFeeNum) || tradeFeeNum < 0)) {
      setFormError('Fee must be ≥ 0.');
      return;
    }

    const priceEdited = price !== initialPriceStr;
    // An untouched price keeps the stored entryAmount; an edited one is
    // denominated in the currency on screen right now.
    const entryFx = await fxForPriceEdit(priceEdited, priceEdited ? prc : tx.entryAmount);
    const priceUsd = entryFx.priceUsd ?? (priceEdited ? prc / rate : tx.price);
    const feeFx = await feeForEdit(tradeFee !== initialTradeFeeStr, tradeFeeNum);
    onSave(tx.id, {
      coinId: selectedCoin.id,
      symbol: selectedCoin.symbol,
      name:   selectedCoin.name,
      type, amount: amt, price: priceUsd, date,
      wallet: wallet || undefined,
      ...entryFx.fx,
      ...feeFx,
    });
    onClose();
  };

  // Price is in the display currency → divide by rate so fmt (USD × rate) renders
  // the Total back in the display currency, matching the Add form.
  const tradeFeeTotalUsd = Math.max(0, parseFloat(tradeFee) || 0) / rate;
  const grossTotalUsd = ((parseFloat(amount) || 0) * (parseFloat(price) || 0)) / rate;
  // Cash out of pocket on a BUY, cash in on a SELL — the same direction the fee
  // moves the taxable gain. Matches the Add form.
  const totalUsd =
    type === 'BUY' ? grossTotalUsd + tradeFeeTotalUsd
    : type === 'SELL' ? grossTotalUsd - tradeFeeTotalUsd
    : grossTotalUsd;
  const showTotal = !!amount && !!price && isFinite(totalUsd) && totalUsd > 0;

  const inputClass =
    'w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-2.5 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-white/20 focus:border-slate-500 transition-colors';
  const selectClass =
    inputClass + ' [color-scheme:dark] cursor-pointer';
  const labelClass = 'block text-xs font-medium text-slate-400 mb-1.5 uppercase tracking-wider';

  // Build the option list for the From dropdown — current selection always present
  const fromOptions = fromWallet && !knownWallets.includes(fromWallet)
    ? [fromWallet, ...knownWallets]
    : knownWallets;
  // To dropdown excludes the chosen From, but keeps the current selection visible
  const toOptionsBase = knownWallets.filter((w) => w !== fromWallet);
  const toOptions = toWallet && !toOptionsBase.includes(toWallet) && toWallet !== fromWallet
    ? [toWallet, ...toOptionsBase]
    : toOptionsBase;

  // BUY/SELL wallet dropdown — same style as the rest of the form
  const handleWalletSelect = (val: string) => {
    setFormError('');
    if (val === ADD_CUSTOM) {
      setShowCustomWallet(true);
      setWallet('');
      return;
    }
    if (val === NO_WALLET) {
      setWallet('');
      return;
    }
    setWallet(val);
  };

  // TRANSFER From dropdown — switches to inline input on "+ Add Custom Wallet"
  const handleFromSelect = (val: string) => {
    setFormError('');
    if (val === ADD_CUSTOM) {
      setShowCustomFrom(true);
      setCustomFromInput('');
      setFromWallet('');
      return;
    }
    setFromWallet(val);
  };
  const confirmCustomFrom = () => {
    const name = customFromInput.trim();
    if (!name) return;
    setFromWallet(name);
    setShowCustomFrom(false);
    setCustomFromInput('');
    setFormError('');
  };
  const cancelCustomFrom = () => {
    setShowCustomFrom(false);
    setCustomFromInput('');
  };

  // TRANSFER To dropdown — same pattern
  const handleToSelect = (val: string) => {
    setFormError('');
    if (val === ADD_CUSTOM) {
      setShowCustomTo(true);
      setCustomToInput('');
      setToWallet('');
      return;
    }
    setToWallet(val);
  };
  const confirmCustomTo = () => {
    const name = customToInput.trim();
    if (!name) return;
    if (name === fromWallet) {
      setFormError('To Wallet cannot be the same as From Wallet.');
      return;
    }
    setToWallet(name);
    setShowCustomTo(false);
    setCustomToInput('');
    setFormError('');
  };
  const cancelCustomTo = () => {
    setShowCustomTo(false);
    setCustomToInput('');
  };

  // The dropdown's value: NO_WALLET when no wallet, the wallet name when known, or ADD_CUSTOM when free-text mode is active
  const walletDropdownValue = showCustomWallet
    ? ADD_CUSTOM
    : !wallet
      ? NO_WALLET
      : wallet;
  const walletOptions = wallet && !knownWallets.includes(wallet) && !showCustomWallet
    ? [wallet, ...knownWallets]
    : knownWallets;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-slate-950 border border-slate-700 rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
          <h2 className="text-base font-semibold text-slate-100">Edit Transaction</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-500 hover:text-slate-200 hover:bg-slate-800 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSave} className="p-6 space-y-4">
          {/* Type toggle */}
          <div>
            <label className={labelClass}>Type</label>
            <div className="flex rounded-xl overflow-hidden border border-slate-700 bg-slate-800">
              {(['BUY', 'SELL', 'TRANSFER'] as const).map((t) => {
                const active = type === t;
                const colour = t === 'BUY'
                  ? (active ? 'bg-emerald-500 text-white' : 'text-slate-500 hover:text-slate-300')
                  : t === 'SELL'
                    ? (active ? 'bg-red-500 text-white' : 'text-slate-500 hover:text-slate-300')
                    : (active ? 'bg-blue-500 text-white' : 'text-slate-500 hover:text-slate-300');
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => { setType(t); setFormError(''); }}
                    className={`flex-1 py-2.5 text-sm font-semibold transition-all flex items-center justify-center gap-1.5 ${colour}`}
                  >
                    {t === 'TRANSFER' && <ArrowRightLeft className="w-3.5 h-3.5" />}
                    {t}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Coin */}
          <div>
            <label className={labelClass}>Coin</label>
            <CoinSelector
              coins={coins}
              metals={metals}
              selectedId={selectedCoin.id}
              onSelect={handleCoinSelect}
              loading={coinsLoading}
              error={coinsError}
              fallbackCoin={selectedCoin}
              restrictTo={type === 'SELL' ? sellableCoins : undefined}
              emptyMessage="No coins in your history to sell."
            />
          </div>

          {/* Amount */}
          <div>
            <label className={labelClass}>Amount ({selectedCoin.symbol})</label>
            <input
              type="number" min="0" step="any" placeholder="0.00"
              value={amount} onChange={(e) => setAmount(e.target.value)}
              className={inputClass}
            />
            {overLimit && (
              <p className="mt-1.5 max-md:mt-1 text-xs max-md:text-[11px] max-md:leading-snug" style={{ color: '#c4615e' }}>
                {type === 'TRANSFER'
                  ? limitMessage(fromWallet, fromBalance, 'transfer')
                  : limitMessage(wallet, sellBalance, 'sell')}
                {(type === 'TRANSFER' ? fromBalance : sellBalance) > WALLET_EPS && (
                  <button
                    type="button"
                    onClick={() => setAmount(String(type === 'TRANSFER' ? fromBalance : sellBalance))}
                    className="ml-1.5 underline underline-offset-2 font-medium"
                    style={{ color: 'inherit' }}
                  >
                    Use max
                  </button>
                )}
              </p>
            )}
            {!overLimit && type === 'TRANSFER' && fromWallet && (
              <p className="mt-1.5 text-xs text-slate-500">
                Available in <span className="text-slate-300">{fromWallet}</span> on {formatDateAU(date)}:{' '}
                <span className="text-slate-300 font-mono">{fmtCoinShort(fromBalance)} {selectedCoin.symbol}</span>
              </p>
            )}
            {!overLimit && type === 'SELL' && wallet && (
              <p className="mt-1.5 text-xs" style={{ color: '#b89968' }}>
                Available on {formatDateAU(date)}: {fmtCoinShort(sellBalance)} {selectedCoin.symbol}
              </p>
            )}
          </div>

          {/* Price (BUY/SELL) */}
          {type !== 'TRANSFER' && (
            <div>
              <label className={labelClass}>Price per coin ({currency.code})</label>
              <input
                type="number" min="0" step="any" placeholder="0.00"
                value={price} onChange={(e) => setPrice(e.target.value)}
                className={inputClass}
              />
            </div>
          )}

          {/* Fee (BUY/SELL) — brokerage/commission, in the display currency */}
          {type !== 'TRANSFER' && (
            <div>
              <label className={labelClass}>Fee ({currency.code}), optional</label>
              <input
                type="number" min="0" step="any" placeholder="0.00"
                value={tradeFee} onChange={(e) => setTradeFee(e.target.value)}
                className={inputClass}
              />
              <p className="mt-1.5 text-xs text-slate-500">
                {type === 'BUY'
                  ? 'Added to your cost base.'
                  : 'Deducted from your proceeds.'}{' '}
                Either way it reduces the taxable gain.
              </p>
            </div>
          )}

          {/* Total preview (BUY/SELL) */}
          {showTotal && type !== 'TRANSFER' && (
            <div className="bg-slate-800/50 rounded-xl px-4 py-3 flex items-center justify-between">
              <span className="text-xs text-slate-500 uppercase tracking-wider">
                Total{tradeFeeTotalUsd > 0 ? (type === 'BUY' ? ' incl. fee' : ' net of fee') : ''}
              </span>
              <span className="text-sm font-semibold text-slate-200">{fmt(totalUsd)}</span>
            </div>
          )}

          {/* TRANSFER: From / To wallet dropdowns */}
          {type === 'TRANSFER' && (
            <>
              <div>
                <label className={labelClass}>From Wallet</label>
                {showCustomFrom ? (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={customFromInput}
                      onChange={(e) => setCustomFromInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); confirmCustomFrom(); }
                        if (e.key === 'Escape') { e.preventDefault(); cancelCustomFrom(); }
                      }}
                      placeholder="Enter wallet name…"
                      autoFocus
                      className={inputClass}
                    />
                    <button
                      type="button"
                      onClick={confirmCustomFrom}
                      disabled={!customFromInput.trim()}
                      className="px-3 py-2.5 text-xs font-semibold text-white bg-blue-500 hover:bg-blue-600 disabled:opacity-40 disabled:cursor-not-allowed rounded-xl transition-colors whitespace-nowrap"
                    >
                      Add
                    </button>
                    <button
                      type="button"
                      onClick={cancelCustomFrom}
                      className="px-3 py-2.5 text-xs text-slate-400 hover:text-slate-200 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-xl transition-colors whitespace-nowrap"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <select
                    value={fromWallet}
                    onChange={(e) => handleFromSelect(e.target.value)}
                    className={selectClass}
                  >
                    <option value="">Select source wallet…</option>
                    {fromOptions.map((w) => {
                      const bal = balanceByWallet[w] ?? 0;
                      return (
                        <option key={w} value={w}>
                          {bal > 0 ? `${w}: ${fmtCoinShort(bal)} ${selectedCoin.symbol}` : w}
                        </option>
                      );
                    })}
                    <option disabled>──────────────</option>
                    <option value={ADD_CUSTOM}>+ Add Custom Wallet</option>
                  </select>
                )}
              </div>

              <div>
                <label className={labelClass}>To Wallet</label>
                {showCustomTo ? (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={customToInput}
                      onChange={(e) => setCustomToInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); confirmCustomTo(); }
                        if (e.key === 'Escape') { e.preventDefault(); cancelCustomTo(); }
                      }}
                      placeholder="Enter wallet name…"
                      autoFocus
                      className={inputClass}
                    />
                    <button
                      type="button"
                      onClick={confirmCustomTo}
                      disabled={!customToInput.trim()}
                      className="px-3 py-2.5 text-xs font-semibold text-white bg-blue-500 hover:bg-blue-600 disabled:opacity-40 disabled:cursor-not-allowed rounded-xl transition-colors whitespace-nowrap"
                    >
                      Add
                    </button>
                    <button
                      type="button"
                      onClick={cancelCustomTo}
                      className="px-3 py-2.5 text-xs text-slate-400 hover:text-slate-200 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-xl transition-colors whitespace-nowrap"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <select
                    value={toWallet}
                    onChange={(e) => handleToSelect(e.target.value)}
                    disabled={!fromWallet}
                    className={selectClass}
                  >
                    <option value="">{fromWallet ? 'Select destination wallet…' : 'Select source wallet first'}</option>
                    {toOptions.map((w) => {
                      const bal = balanceByWallet[w] ?? 0;
                      return (
                        <option key={w} value={w}>
                          {bal > 0 ? `${w}: ${fmtCoinShort(bal)} ${selectedCoin.symbol}` : w}
                        </option>
                      );
                    })}
                    <option disabled>──────────────</option>
                    <option value={ADD_CUSTOM}>+ Add Custom Wallet</option>
                  </select>
                )}
              </div>
            </>
          )}

          {/* Date */}
          <div>
            <label className={labelClass}>Date</label>
            <input
              type="date" value={date} max={today}
              onChange={(e) => setDate(e.target.value)}
              className={inputClass + ' [color-scheme:dark]'}
            />
          </div>

          {/* Wallet (BUY/SELL) — dropdown of known wallets, or free-text via "Add custom…" */}
          {type !== 'TRANSFER' && (
            <div>
              <label className={labelClass}>Wallet / Exchange</label>
              {showCustomWallet ? (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={wallet}
                    onChange={(e) => setWallet(e.target.value)}
                    placeholder="Enter wallet name…"
                    autoFocus
                    className={inputClass}
                  />
                  <button
                    type="button"
                    onClick={() => { setShowCustomWallet(false); setWallet(''); }}
                    className="px-3 py-2.5 text-xs text-slate-400 hover:text-slate-200 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-xl transition-colors whitespace-nowrap"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <select
                  value={walletDropdownValue}
                  onChange={(e) => handleWalletSelect(e.target.value)}
                  className={selectClass}
                >
                  <option value={NO_WALLET}>No wallet assigned</option>
                  {walletOptions.map((w) => (
                    <option key={w} value={w}>{w}</option>
                  ))}
                  <option value={ADD_CUSTOM}>+ Add Custom…</option>
                </select>
              )}
            </div>
          )}

          {/* TRANSFER: Network fee + notes */}
          {type === 'TRANSFER' && (
            <>
              <div>
                <label className={labelClass}>Network Fee ({currency.code}), optional</label>
                <input
                  type="number" min="0" step="any" placeholder="0.00"
                  value={networkFee}
                  onChange={(e) => setNetworkFee(e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Notes, optional</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  className={inputClass + ' resize-none'}
                />
              </div>
            </>
          )}

          {formError && (
            <p className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
              {formError}
            </p>
          )}

          <div className="flex gap-3 pt-1">
            <button
              type="button" onClick={onClose}
              className="flex-1 py-2.5 text-sm font-semibold text-slate-400 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-xl transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-semibold rounded-xl transition-colors"
              style={{ backgroundColor: '#d4b483', color: '#1a1410' }}
            >
              <Save className="w-3.5 h-3.5" />
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
